import React, { useCallback, useEffect, useMemo, useState } from 'react';
import api from '../api';
import { useAuth } from '../context/AuthContext';

const CURRENT_YEAR = new Date().getFullYear();
const WEEKDAYS = ['일', '월', '화', '수', '목', '금', '토'];
const TYPECLS_OPTIONS = ['정규', '특별', '보충', '방과후', '행사', '휴업', '기타'];

const TYPECLS_CLASS = {
  정규: 'typecls-regular',
  특별: 'typecls-special',
  보충: 'typecls-supplement',
  방과후: 'typecls-afterschool',
  행사: 'typecls-event',
  휴업: 'typecls-closure',
  기타: 'typecls-etc',
};

function typeclsClassName(typecls) {
  return TYPECLS_CLASS[typecls] || 'typecls-etc';
}

function pad2(n) {
  return String(n).padStart(2, '0');
}

function toDateStr(year, month, day) {
  return `${year}-${pad2(month)}-${pad2(day)}`;
}

// 1학기: 해당 연도 3월~7월, 2학기: 해당 연도 8월~다음 연도 1월
function getSemesterMonths(year, semester) {
  if (semester === 1) {
    return [3, 4, 5, 6, 7].map((month) => ({ year, month }));
  }
  return [8, 9, 10, 11, 12, 1].map((month) => ({
    year: month === 1 ? year + 1 : year,
    month,
  }));
}

function getSemesterRange(year, semester) {
  const months = getSemesterMonths(year, semester);
  const first = months[0];
  const last = months[months.length - 1];
  const lastDay = new Date(last.year, last.month, 0).getDate();
  return { start: toDateStr(first.year, first.month, 1), end: toDateStr(last.year, last.month, lastDay) };
}

export default function AcademicCalendar() {
  const { user } = useAuth();
  const canEdit = user?.role === 'admin' || user?.role === 'staff';

  const [year, setYear] = useState(CURRENT_YEAR);
  const [semester, setSemester] = useState(1);
  const [grade, setGrade] = useState(1);
  const [scheduleByDate, setScheduleByDate] = useState({});
  const [activeDate, setActiveDate] = useState(null);
  const [newTypecls, setNewTypecls] = useState('정규');
  const [newTitle, setNewTitle] = useState('');

  const months = useMemo(() => getSemesterMonths(year, semester), [year, semester]);

  const loadSchedule = useCallback(() => {
    const { start, end } = getSemesterRange(year, semester);
    api.get('/admin/academic-schedule', { params: { start, end, grade } }).then((res) => {
      const grouped = {};
      res.data.forEach((e) => {
        grouped[e.schedate] = grouped[e.schedate] || [];
        grouped[e.schedate].push(e);
      });
      setScheduleByDate(grouped);
    });
  }, [year, semester, grade]);

  useEffect(() => {
    loadSchedule();
  }, [loadSchedule]);

  const openDay = (dateStr) => {
    setActiveDate(dateStr);
    setNewTitle('');
  };

  const closeDay = () => {
    setActiveDate(null);
    setNewTitle('');
  };

  const handleAdd = async () => {
    await api.post('/admin/academic-schedule', {
      schedate: activeDate,
      grade,
      typecls: newTypecls,
      title: newTitle.trim() || null,
    });
    setNewTitle('');
    await loadSchedule();
  };

  const handleDelete = async (id) => {
    await api.delete(`/admin/academic-schedule/${id}`);
    await loadSchedule();
  };

  const activeEntries = activeDate ? scheduleByDate[activeDate] || [] : [];

  return (
    <div className="page">
      <div className="page-title-row">
        <label>연도</label>
        <select value={year} onChange={(e) => setYear(Number(e.target.value))}>
          {[CURRENT_YEAR - 1, CURRENT_YEAR, CURRENT_YEAR + 1].map((y) => (
            <option key={y} value={y}>{y}</option>
          ))}
        </select>
        <select value={semester} onChange={(e) => setSemester(Number(e.target.value))}>
          <option value={1}>1학기</option>
          <option value={2}>2학기</option>
        </select>
        <select value={grade} onChange={(e) => setGrade(Number(e.target.value))}>
          <option value={1}>1학년</option>
          <option value={2}>2학년</option>
          <option value={3}>3학년</option>
        </select>
        <h2>학사일정</h2>
      </div>

      <div className="card">
        <div className="academic-calendar-semester">
          {months.map(({ year: y, month: m }) => (
            <MonthCalendar
              key={`${y}-${m}`}
              year={y}
              month={m}
              scheduleByDate={scheduleByDate}
              onDayClick={openDay}
            />
          ))}
        </div>
      </div>

      {activeDate && (
        <div className="modal-overlay" onClick={closeDay}>
          <div className="modal-card" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{activeDate} · {grade}학년 학사일정</h3>
              <button className="btn-ghost" type="button" onClick={closeDay}>닫기</button>
            </div>

            {activeEntries.length === 0 && (
              <div className="empty-row">등록된 학사일정이 없습니다.</div>
            )}
            {activeEntries.map((e) => (
              <div key={e.id} className="academic-calendar-entry-row">
                <span>
                  <span className={`academic-calendar-typecls-badge ${typeclsClassName(e.typecls)}`}>
                    {e.typecls}
                  </span>
                  {e.title && <span> {e.title}</span>}
                </span>
                {canEdit && (
                  <button className="btn-link" type="button" onClick={() => handleDelete(e.id)}>삭제</button>
                )}
              </div>
            ))}

            {canEdit && (
              <div className="academic-calendar-add-row">
                <select value={newTypecls} onChange={(e) => setNewTypecls(e.target.value)}>
                  {TYPECLS_OPTIONS.map((t) => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
                <input
                  type="text"
                  className="field-input"
                  value={newTitle}
                  onChange={(e) => setNewTitle(e.target.value)}
                  placeholder="비고 (선택 입력)"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleAdd();
                  }}
                />
                <button className="btn-primary btn-inline" type="button" onClick={handleAdd}>추가</button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function MonthCalendar({ year, month, scheduleByDate, onDayClick }) {
  const firstWeekday = new Date(year, month - 1, 1).getDay();
  const daysInMonth = new Date(year, month, 0).getDate();

  const cells = [];
  for (let i = 0; i < firstWeekday; i += 1) cells.push(null);
  for (let d = 1; d <= daysInMonth; d += 1) cells.push(d);
  while (cells.length % 7 !== 0) cells.push(null);

  const weeks = [];
  for (let i = 0; i < cells.length; i += 7) weeks.push(cells.slice(i, i + 7));

  return (
    <div className="academic-calendar-month">
      <h4>{year}년 {month}월</h4>
      <table className="academic-calendar-table">
        <thead>
          <tr>
            {WEEKDAYS.map((w) => (
              <th key={w}>{w}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {weeks.map((week, wi) => (
            <tr key={wi}>
              {week.map((d, di) => {
                if (d === null) return <td key={di} className="academic-calendar-empty" />;
                const dateStr = toDateStr(year, month, d);
                const entries = scheduleByDate[dateStr] || [];
                return (
                  <td key={di} className="academic-calendar-day" onClick={() => onDayClick(dateStr)}>
                    <div className="academic-calendar-daynum">{d}</div>
                    {entries.map((e) => (
                      <div
                        key={e.id}
                        className={`academic-calendar-entry ${typeclsClassName(e.typecls)}`}
                      >
                        {e.typecls}{e.title ? ` - ${e.title}` : ''}
                      </div>
                    ))}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
